import { IMAXAServerMessage, IMAXAQuestion, IMAXAQuestionPayload, choice_question, input_question, IMAXALockLevel, IMAXAQuestionType } from '@core/Logic/Links/IMAXA/IMAXAProtocol';
import { CrazyMegaEpicScalingStructure, GetParameterByName, GetScalingInfo } from '@core/Utils/HelperFunction'
import { PasswordDialog } from '@core/GUI/Dialogs/PasswordDialog';
import { MiscButtonsContainer } from '@core/GUI/MiscButtonsContainer';
import { MessageController, MESSAGE_SEVERITY_LEVEL } from '@core/GUI/Dialogs/MessageController'
import { Language, Messages } from '@core/GUI/Language';
import { ChoiceQuestion, QuestionDialog } from '@core/GUI/Dialogs/QuestionDialog';
import { PinCode } from '@core/GUI/Dialogs/PinCode';
import { MainScene } from '@core/Scenes/MainScene';
import { Constants, effectsVolume, musicVolume, virtualWheel } from '@core/Utils/Constants';
import { WinDisplay } from "@core/GUI/WinDisplay"
import { Phase } from '@core/Logic/Logic';
import { DemoMode } from '@core/GUI/DemoMode';
import { Board } from '../Board';
import { Track } from '../Track';
import { BaseRouletteHelpScreen } from '../GUI/Pages/BaseRouletteHelpScreen';
import { MenuDialog } from '@core/GUI/Dialogs/MenuDialog';
import { Client } from '@core/Client';
import { LuckyNumberJackpot } from '../Modules/LuckyNumberJackpot';
import { RouletteIntroPrompt } from '../GUI/Dialogs/RouletteIntroPrompt';
import { TopPanel } from '../GUI/Panels/TopPanel';
import { HistoryDialog } from '@core/GUI/Dialogs/HistoryDialog';

export class BaseRouletteMainScene extends MainScene {
	public board: Board;
	public panel: TopPanel;
	public track: Track;
	public backgroundMusic: Phaser.Sound.BaseSound[] | Phaser.Sound.HTML5AudioSound[] | Phaser.Sound.WebAudioSound[];
	public activeQuestionDialog: QuestionDialog;

	protected historyDialog: HistoryDialog;
	protected passwordDialog: PasswordDialog;
	protected winDisplay: WinDisplay;
	protected demoModeText: DemoMode;
	protected domPayoutLockInfo: Phaser.GameObjects.DOMElement;
	protected messageController: MessageController;

	protected previousHistoryVisibility: boolean = false;
	protected boardVisibility;
	protected trackVisibility;
	protected boardAlpha;
	protected trackAlpha;

	private miscButtons: MiscButtonsContainer;
	private backgroundMusicIndex = 0;
	private backgroundMusicSeek = 0;
	private minTableBet: Phaser.GameObjects.Text;
	private maxTableBet: Phaser.GameObjects.Text;
	private errorTextDisplay: Phaser.GameObjects.Text;

	protected lockMessage:string;
	protected lockReason:string;

	constructor() {
		super();
	}

	protected UpdateHistoryView(render: boolean): void {
		if (!this.previousHistoryVisibility) {
			this.boardVisibility = this.board.visible;
			this.trackVisibility = this.track.visible;
			this.boardAlpha = this.board.alpha;
			this.trackAlpha = this.track.alpha;
		}

		if (Client.logic.historyShown && render) {
			let stuffToDraw: Array<Phaser.GameObjects.GameObject> = new Array<Phaser.GameObjects.GameObject>();
			stuffToDraw.push(this.backgroundImage);
			stuffToDraw.push(this.panel.balanceDisplay);

			this.panel.balanceDisplay.HistoricMode(true);
			this.panel.Update(16);

			let boardVisibility = this.board.visible;
			let trackVisibility = this.track.visible;
			this.board.setVisible(true);
			this.track.setVisible(true);
			let boardAlpha = this.board.alpha;
			let trackAlpha = this.track.alpha;
			this.board.setAlpha(1);
			this.track.setAlpha(1);

			if (Client.logic.IsBoard)
				stuffToDraw.push(this.board);
			else
				stuffToDraw.push(this.track);

			this.historyDialog.Show(stuffToDraw);
			this.previousHistoryVisibility = true;

			this.panel.balanceDisplay.HistoricMode(false);
			this.board.setVisible(false);
			this.track.setVisible(false);
			this.panel.balanceDisplay.setVisible(false);
			Client.logic.lockAnimations = true;

			if (!virtualWheel && this.panel && this.panel.videoFeed)
				this.panel.HideVideo();
		} else {
			this.historyDialog.Hide();

			this.previousHistoryVisibility = false;
			this.board.setVisible(this.boardVisibility);
			this.track.setVisible(this.trackVisibility);
			this.board.setAlpha(this.boardAlpha);
			this.track.setAlpha(this.trackAlpha);

			this.panel.balanceDisplay.setVisible(true);
			Client.logic.lockAnimations = false;

			if (!virtualWheel && this.panel && this.panel.videoFeed)
				this.panel.ShowVideo();
		}
	}

	protected override OnQuestion(question: IMAXAServerMessage): void {
		Client.logic.lockAnimations = true;
		//console.log("GOT QUESTION");
		let data: IMAXAQuestionPayload = question.payload as IMAXAQuestionPayload;

		if (data.type == IMAXAQuestionType.choice) {
			this.input.enabled = true;
			if (this.panel)
				this.panel.HideVideo();
			this.activeQuestionDialog = new ChoiceQuestion(this, (data) => {
				Client.remote.AnswerQuestion(data);
				//remove question dialogue
				this.activeQuestionDialog.visible = false;
				this.children.remove(this.activeQuestionDialog);
				this.activeQuestionDialog.destroy();
				this.activeQuestionDialog = null;
				if (this.panel)
					this.panel.ShowVideo();
				Client.logic.lockAnimations = false;
				Client.logic.playerPromptsWaiting -= 1;
				Client.remote.RemoteActionsHaveChanged();
			}, this.staticWidth, this.staticHeight, this.portraitMode, (question as IMAXAQuestion).payload as choice_question);
			this.children.add(this.activeQuestionDialog);
			this.children.bringToTop(this.activeQuestionDialog);
			this.resize(GetScalingInfo());
			Client.logic.playerPromptsWaiting += 1;
			Client.remote.RemoteActionsHaveChanged();
		}
		else if (data.type == IMAXAQuestionType.input) {
			if ((question.payload as input_question).topic == "lock-code") {
				this.displayRegistrationDialog((question.payload as input_question).question, data);
			}
			else if ((question.payload as input_question).topic == "unlock-code") {
				this.displayLoginPasswordDialog((question.payload as input_question).question, data);
			}
		}
	}

	public contextGiven(): void {
		if (this.game.sound) {
			this.game.sound.add(Constants.SOUNDS.BET_PLACE_GROUP, { volume: 1.0 * effectsVolume });

			this.game.sound.add(Constants.SOUNDS.WIN_1, { volume: 0.45 * effectsVolume });
			this.game.sound.add(Constants.SOUNDS.WIN_2, { volume: 0.45 * effectsVolume });
			this.game.sound.add(Constants.SOUNDS.WIN_3, { volume: 0.45 * effectsVolume });
			this.game.sound.add(Constants.SOUNDS.CHIPS_DROPS, { volume: 0.45 * effectsVolume });
		}

		this.initialBackgroundMusic();

		if (this.panel)
			this.panel.contextGiven();
		if (this.activeQuestionDialog && this.panel)
			this.panel.HideVideo();

		if (this.helpScreen && this.helpScreen.hidden && !this.activeQuestionDialog) {
			if (Client.logic.Phase != Phase.SHOWING_HISTORY_REPLAY) {
				this.panel.ShowVideo();
			}
			if (Client.logic.showStatistics) {
				this.panel.toggleStatisticsDisplay();
			}
		}

		this.game.events.emit(Constants.EVENTS.PANEL_UPDATE_BALANCE, false);

		this.game.events.on("visible", () => {
			console.log("Game is visible!");
			this.turnOnSound();
			if (this.helpScreen && this.helpScreen.hidden) {
				if (Client.logic.Phase != Phase.SHOWING_HISTORY_REPLAY && !this.activeQuestionDialog) this.panel.ShowVideo()
				if (Client.logic.showStatistics) this.panel.toggleStatisticsDisplay();
			}
			this.game.events.emit(Constants.EVENTS.PANEL_UPDATE_BALANCE, false);
		});
		this.game.events.on("hidden", () => {
			console.log("Game is hidden!");
			this.turnOffSound();
			if (this.panel)
				this.panel.HideVideo()
		});

		//We are ready
		Client.game.events.emit(Constants.EVENTS.MAIN_SCENE_READY, this);
	}

	public override create(): void {
		super.create();

		this.sound.pauseOnBlur = false;
		this.game.sound.pauseOnBlur = false;

		this.input.setTopOnly(false);
		this.input.setGlobalTopOnly(false);

		this.messageController = new MessageController(this, 100, 100, 3);
		this.children.add(this.messageController);

		this.input.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
			// Used for notifying the chat box input field
			this.game.events.emit(Constants.EVENTS.CLICK_EVENT_FOR_CHAT_BOX)
			if (this.panel && this.game.device.os.iOS) {
				this.panel.NotifyVideo()
			}
		});
		this.game.events.addListener("FADE_BACKGROUND_MUSIC", (fadeIn: boolean) => { this.FadeBackgroundMusic(fadeIn) });

		this.resize(GetScalingInfo());

		//Main scene is fully created but not yet ready
		Client.game.events.emit(Constants.EVENTS.MAIN_SCENE_ACTIVE, this);
	}

	public override ModulesUpdated(): void {
		if (this.helpScreen && !this.helpScreen.hasJackpotPage) {
			Client.logic.modules.forEach(module => {
				if (module instanceof LuckyNumberJackpot && module.ready) {
					(this.helpScreen as BaseRouletteHelpScreen).addLuckyJackpotPage();
					this.helpScreen.hasJackpotPage = true;
				}
			});
		}
	}

	public MuteBackgroundMusic(): void {
		if (this.backgroundMusic) {
			this.backgroundMusic.forEach((sound: Phaser.Sound.BaseSound | Phaser.Sound.HTML5AudioSound | Phaser.Sound.WebAudioSound) => {
				(sound as Phaser.Sound.HTML5AudioSound | Phaser.Sound.WebAudioSound).setMute(true);
				(sound as Phaser.Sound.HTML5AudioSound | Phaser.Sound.WebAudioSound).stop();
			})
		}
	}

	public UnmuteBackgroundMusic(index?: number): void {
		if (this.backgroundMusic) {
			(this.backgroundMusic[index] as Phaser.Sound.HTML5AudioSound | Phaser.Sound.WebAudioSound).setMute(false);
			(this.backgroundMusic[index] as Phaser.Sound.HTML5AudioSound | Phaser.Sound.WebAudioSound).play();
			this.backgroundMusicIndex = index;
		}
	}

	public FadeBackgroundMusic(fadeIn: boolean): void {
		if (this.backgroundMusic) {
			if (fadeIn) {
				this.add.tween({
					targets: this.backgroundMusic,
					volume: 0.10 * musicVolume,
					// delay: 0,
					duration: 800,
				})
			} else {
				this.add.tween({
					targets: this.backgroundMusic,
					volume: 0,
					duration: 10,
				})
			}
		}
	}

	protected turnOffSound() {
		// Save the duration at which we stopped the background music to later resume from here instead of restarting the sound
		if (this.backgroundMusic) this.backgroundMusicSeek = (this.backgroundMusic[this.backgroundMusicIndex] as Phaser.Sound.HTML5AudioSound | Phaser.Sound.WebAudioSound).seek
		this.game.sound.stopAll();
		this.game.sound.volume = 0;
		this.sound.stopAll();
		this.sound.volume = 0;
	}

	protected turnOnSound() {
		if (!this.registry.get("is_game_muted")) {
			this.game.sound.volume = 1;
			this.sound.volume = 1;
			this.game.sound.stopAll();
			this.sound.stopAll();
			// If defined means first connect happened
			if (this.backgroundMusic) {
				this.backgroundMusic[this.backgroundMusicIndex].play();
				(this.backgroundMusic[this.backgroundMusicIndex] as Phaser.Sound.HTML5AudioSound | Phaser.Sound.WebAudioSound).setSeek(this.backgroundMusicSeek)
			}
		}
	}

	public override ProcessSettings() {
		if (!this.firstConnect)
		{
			this.resize(GetScalingInfo());
			Client.logic.refresh();
			return;
		}

		this.createCommonChildren();

		this.winDisplay = new WinDisplay(this.scene.scene);

		if (Client.logic.setup.USER.Demo) this.createDemoMode();

		this.scale.fullscreenTarget = document.getElementById("game");

		this.board = new Board(this.scene.scene, this.textures);
		this.board.setVisible(true);
		this.children.add(this.board);

		this.track = new Track(this.scene.scene, this.textures);
		this.track.setVisible(false);
		this.children.add(this.track);

		this.helpScreen = new BaseRouletteHelpScreen(this.scene.scene);
		if (!this.helpScreen.hasJackpotPage) {
			Client.logic.modules.forEach(module => {
				if (module instanceof LuckyNumberJackpot && module.ready) {
					(this.helpScreen as BaseRouletteHelpScreen).addLuckyJackpotPage();
					this.helpScreen.hasJackpotPage = true;
				}
			});
		}

		this.menuDialog = new MenuDialog(this, 5, 5, 100, 100);
		this.children.add(this.menuDialog);

		this.panel = new TopPanel(this, this.menuDialog, this.board, this.track);
		this.children.add(this.panel);
		this.children.sendToBack(this.panel);

		this.miscButtons = new MiscButtonsContainer(this.scene.scene, this.game.events);
		this.children.add(this.miscButtons);

		this.game.events.on(Constants.EVENTS.TOGGLE_BOARD_TRACK_VIEW, () => { this.onToggleBoardTrackView() });

		this.game.events.on(Constants.EVENTS.ON_LOGOUT, () => {
			let text = Language.Get(Messages.CLICK_TO_RESTART_GAME);
			let html = `<div><a style="color: white; font-size: 35px; font-family: 'Arial', sans-serif; text-decoration: none;" href="javascript:window.location.reload(true)">${text}</a></div>`;
			let refreshButton = this.add.dom(this.scale.width / 2, this.scale.height / 2).createFromHTML(html);
			refreshButton.setInteractive();
			refreshButton.setOrigin(0.5);
			refreshButton.on("pointerdown", (pointer) => {
				window.location.reload()
			});
			refreshButton.setPosition(this.scale.width / 2, this.scale.height - refreshButton.displayHeight);
			window.addEventListener('resize', () => refreshButton.setPosition(this.scale.width / 2, this.scale.height - refreshButton.displayHeight));
			this.children.bringToTop(refreshButton);
		}, this);

		this.input.on('pointerdown', function (pointer: any) {
			this.game.events.emit(Constants.EVENTS.SCENE_GLOBAL_POINTER_DOWN_EVENT);
		}.bind(this));

		this.input.on('pointerup', function (pointer: any) {
			this.game.events.emit(Constants.EVENTS.SCENE_GLOBAL_POINTER_UP_EVENT);
		}.bind(this));

		this.game.events.on("authEvent", (data: { registration: string }) => { this.authenticationHandler(data) });

		if (!Client.logic.IsSpectator)
			Client.logic.refresh();

		/*******************DECLARE ANIMATIONS HERE********************/
		this.anims.create({
			key: "coldanim",
			frames: this.anims.generateFrameNames(Constants.ANIMATIONS.COLD_ANIMATION, {
				start: 0,
				end: 49,
				suffix: ".png"
			}),
			frameRate: 25,
			repeat: -1
		})

		this.anims.create({
			key: "hotanim",
			frames: this.anims.generateFrameNames(Constants.ANIMATIONS.HOT_ANIMATION, {
				start: 0,
				end: 49,
				suffix: ".png"
			}),
			frameRate: 25,
			repeat: -1
		})
		/**************************************************************/

		this.children.bringToTop(this.menuDialog);

		// Refresh min and max bet infos on stake changed
		this.game.events.on(Constants.EVENTS.STAKE_CHANGED, () => {
			if (this.game.device.os.desktop && this.minTableBet && this.maxTableBet) {
				if (Client.logic.Stake.PlayBoardLimitMin == 0) {
					// Do not display anything if there is no playboard min limit
					this.minTableBet.text = ""
				} else {
					this.minTableBet.text = Language.Get(Messages.MIN_TABLE_BET) + ": " + Language.InternationalNumberFormat.format(Client.logic.BalanceManager.CreditToMoney(Client.logic.Stake.PlayBoardLimitMin))
				}
				if (Client.logic.Stake.PlayBoardLimitMax == 0) {
					// Do not display anything if there is no playboard max limit
					this.maxTableBet.text = ""
				} else {
					this.maxTableBet.text = Language.Get(Messages.MAX_TABLE_BET) + ": " + Language.InternationalNumberFormat.format(Client.logic.BalanceManager.CreditToMoney(Client.logic.Stake.PlayBoardLimitMax))
				}
			}
		}, this);
		//-----------------------------------------------------

		//-----Add intro prompt--------------------------------
		let userAgent = navigator.userAgent.toLowerCase()
		if (userAgent.indexOf(' electron/') > -1 || !this.firstConnect) {
			this.contextGiven();
		} else {
			let onClickCallback = () => {
				if (!this.scale.isFullscreen && !this.game.device.os.iOS && !this.game.device.os.desktop)
					this.scale.toggleFullscreen();
				this.introPrompt.destroy();
				this.introPrompt = null;
				this.contextGiven();
			}

			this.introPrompt = new RouletteIntroPrompt(this, onClickCallback);
			this.children.add(this.introPrompt);
			this.children.bringToTop(this.introPrompt);
		}
		//-----------------------------------------------------

		//-----Setup history dialog----------------------------
		this.game.events.on(Constants.EVENTS.RENDER_HISTORY_VIEW, (render) => this.UpdateHistoryView(render), this);

		this.historyDialog = new HistoryDialog(this, Constants.SPRITESHEET_MENU, "MenuIcon_PreviousPage.png", "MenuIcon_Close.png", "MenuIcon_Repeat.png", Constants.MENU_BUTTON_STYLE_FONT);
		this.historyDialog.historyBackButton.on("pointerup", (p, x, y, e) => {
			e.stopPropagation();
			Client.logic.NextHistorySnapshot();
			this.UpdateHistoryView(true);
		}, this)

		this.historyDialog.historyForwardButton.on("pointerup", (p, x, y, e) => {
			e.stopPropagation()
			Client.logic.PreviousHistorySnapshot();
			this.UpdateHistoryView(true);
		}, this)
		//-----------------------------------------------------

		setTimeout(() => {
			this.resize(GetScalingInfo());
		}, 1000);
		this.resize(GetScalingInfo());

		this.firstConnect = false;

		this.addSlotExitButton();
	}

	protected addSlotExitButton():void{
		if (Client.slot) {
			this.slotExitButton = new Phaser.GameObjects.Image(this, 0, 0, Constants.SLOTATLAS, "btn_back_menu.png").setOrigin(0, 0);
			this.slotExitButton.setInteractive();
			this.slotExitButton.on("pointerdown", (m, x, y, e) => {
				this.slotExitButton.setTexture(Constants.SLOTATLAS, "btn_back_menu_pressed.png");
				e.stopPropagation();
			}, this);
			this.slotExitButton.on("pointerout", (m, x, y, e) => {
				this.slotExitButton.setTexture(Constants.SLOTATLAS, "btn_back_menu.png");
			}, this);
			this.slotExitButton.on("pointerup", (m, x, y, e) => {
				e.stopPropagation();
				this.slotExitButton.setTexture(Constants.SLOTATLAS, "btn_back_menu.png");
				this.sound.play(Constants.SOUNDS.BUTTON_PRESS_SOFT, { volume: 0.8 * effectsVolume });
				Client.logic.ClickedOnExit();
			}, this);
			this.children.add(this.slotExitButton);
		}
	}

	protected displayErrorMessage(text: string): void {
		if (this.errorTextDisplay) {
			this.errorTextDisplay.setVisible(false)
			return
		}
		if (this.errorTextDisplay) {
			this.errorTextDisplay.setVisible(true)
			this.errorTextDisplay.text = text;
		} else {
			this.errorTextDisplay = new Phaser.GameObjects.Text(this.scene.scene, 0, 0, text, (Constants.LOAD_GAME.ERROR_TEXT_FONT as Phaser.GameObjects.TextStyle))
			this.errorTextDisplay.setVisible(true)
		}

		this.resizeText(this.staticWidth, this.staticHeight, this.portraitMode)
		this.children.add(this.errorTextDisplay)
	}

	protected resizeText(width: number, height: number, portraitMode: boolean): void {
		if (this.errorTextDisplay) {
			this.errorTextDisplay.style.setFontSize(Math.floor(Math.min(Math.max(1, ((width * 0.90) / this.errorTextDisplay.displayWidth) * parseInt(this.errorTextDisplay.style.fontSize)), 30)))
			let ew = this.errorTextDisplay.displayWidth
			let eh = this.errorTextDisplay.displayHeight

			if (portraitMode)
				this.errorTextDisplay.setPosition(width / 2 - ew / 2, height / 2 - eh / 2 - height);
			else
				this.errorTextDisplay.setPosition(width / 2 - ew / 2, height / 2 - eh / 2);
		}
	}

	private initializePasswordDialog(): void {
		this.passwordDialog = new PasswordDialog(new PinCode(this.scene.scene))
		this.passwordDialog.Resize(this.staticWidth, this.staticHeight)
	}

	protected authenticationHandler(data: { registration: string }): void {
		if (data && data.registration) {
			this.displayRegistrationDialog()
		} else {
			this.displayLoginPasswordDialog()
		}
	}

	private sendAuthenticationRequest(user: string, pass: string, callback?: (_ok: boolean, data: IMAXAServerMessage) => void): void {
		Client.remote.SendRequest(Constants.CLIENT_REQUESTS.AUTH, { user: user, pass: pass }, callback)
	}

	private submitPassword(password: string, question): void {
		this.passwordDialog.Hide()
		// if (password === null) return
		let username = GetParameterByName("pid")
		if (question) {
			let lockObject = { question: question.ID, answer: password };
			Client.remote.AnswerQuestion(lockObject);
		}
		else {
			this.sendAuthenticationRequest(username, password, (_ok: boolean, data: IMAXAServerMessage) => {
				if (!_ok) {
					// This should be a description of the error
					console.log(data)
					this.displayErrorMessage(Language.Get(Messages.AUTHENTICATION_PROBLEM) + " " + (data ? data : ""))
				} else {
					if (data.payload.success == false) {
						// this.displayErrorMessage("Wrong password!")
						if (password && password.length > 0) this.game.events.emit(Constants.EVENTS.SHOW_MESSAGE_BOX, Language.Get(Messages.WRONG_PASSWORD), MESSAGE_SEVERITY_LEVEL.WARNING)
						return
					}
				}
			})
		}
	}

	private checkLoginPassword(password: string, question?): void {
		// Cancel button pressed means send null password ''
		if (password === null) {
			this.submitPassword('', question)
			return
		} else if (password.length == 0) {
			this.displayLoginPasswordDialog("Empty password entered, try again".toUpperCase())
			return
		}
		this.submitPassword(password, question)
	}

	private displayLoginPasswordDialog(message?: string, question?): void {
		if (!this.passwordDialog) this.initializePasswordDialog()

		let callback = (password: string) => {
			this.checkLoginPassword(password, question)
		}
		this.passwordDialog.UpdateCallback(callback)
		if (Client.logic.payoutRequested)
			this.passwordDialog.Show(this.staticWidth, this.staticHeight, message ? message : Language.Get(Messages.ENTER_PIN), Language.Get(Messages.ENTER_PIN_SUBTEXT), "", Language.Get(Messages.PAYOUT_REQUESTED))
		else
			this.passwordDialog.Show(this.staticWidth, this.staticHeight, message ? message : Language.Get(Messages.ENTER_PIN), Language.Get(Messages.ENTER_PIN_SUBTEXT), "", Language.Get(Messages.ENTER_PIN))
	}

	private submitRegistrationPassword(password: string, question): void {
		this.passwordDialog.Hide();
		let username = GetParameterByName("pid");
		if (question) {
			let lockObject = { question: question.ID, answer: password };
			Client.remote.AnswerQuestion(lockObject);
		}
		else {
			this.sendAuthenticationRequest(username, password, (_ok: boolean, data: IMAXAServerMessage) => {
				if (!_ok) {
					// This should be a description of the error
					console.log(data);
					this.displayErrorMessage(Language.Get(Messages.AUTHENTICATION_PROBLEM) + " " + (data ? data : ""));
				} else {
					if (data.payload.success == false) {
						// Temporary "solution"
						// this.displayErrorMessage("Wrong password!")
						return
					}
				}
			});
		}
	}

	private checkRegistrationConfirmation(firstPassword: string, secondPassword: string, question?): void {
		if (firstPassword && secondPassword) {
			if (firstPassword == secondPassword) {

				if (!question) {
					let notice = `Thank you for creating an account. Write down your pin, you will need it on payout.`
					this.events.emit(Constants.EVENTS.SHOW_MESSAGE_BOX, notice, MESSAGE_SEVERITY_LEVEL.WARNING)
				};
				this.submitRegistrationPassword(firstPassword, question);
				return
			} else {
				this.displayRegistrationDialog("Entries do not match, try again".toUpperCase(), question)
				return
			}
		} else {
			this.displayRegistrationDialog("Entries do not match, try again".toUpperCase(), question)
		}
	}

	private displayRegistrationConfirmation(firstPassword: string, question?): void {
		let callback = (secondPassword: string) => {
			this.checkRegistrationConfirmation(secondPassword, firstPassword, question)
		}
		this.passwordDialog.UpdateCallback(callback)
		this.passwordDialog.Show(this.staticWidth, this.staticHeight, Language.Get(Messages.CONFIRM_PIN), Language.Get(Messages.CONFIRM_PIN_SUBTEXT), "", Language.Get(Messages.CONFIRM_PIN))
	}

	private checkRegistrationPassword(password: string, question?): void {
		if (!password) {
			this.displayRegistrationDialog(null, question)
			return
		} else {
			this.displayRegistrationConfirmation(password, question)
		}
	}

	private displayRegistrationDialog(message?: string, question?): void {
		if (!this.passwordDialog) this.initializePasswordDialog()

		this.passwordDialog.UpdateCallback((password: string) => {
			if (password == null && question) {
				console.log("ANSWERING EMPTY");
				let lockObject = { question: question.ID, answer: '' };
				Client.remote.AnswerQuestion(lockObject);
				this.passwordDialog.Hide();
			}
			else
				this.checkRegistrationPassword(password, question)
		});

		this.passwordDialog.Show(this.staticWidth, this.staticHeight, message ? "" : Language.Get(Messages.SET_PIN), message ? message : Language.Get(Messages.SET_PIN_SUBTEXT), "", Language.Get(Messages.SET_PIN))
	}

	protected createDemoMode(): void {
		this.demoModeText = new DemoMode(this.scene.scene);
		this.demoModeText.setVisible(true);
		this.demoModeText.setDepth(-9);
		this.children.addAt(this.demoModeText, 0);
	}

	public onToggleBoardTrackView(): void {
		Client.logic.IsBoard = (!this.board.visible);
		if (this.panel) {
			this.panel.OnBoardTypeToggle(Client.logic.IsBoard);
		}

		if (this.miscButtons)
			this.miscButtons.OnBoardTypeToggle(Client.logic.IsBoard);

		let tweenDuration = 200;

		if (Client.logic.IsBoard) {
			this.board.setVisible(true)
			this.board.alpha = 0;
			this.tweens.add({
				targets: this.board,
				duration: tweenDuration,
				alpha: 1,
				onComplete: () => {
					this.board.setVisible(Client.logic.IsBoard);
					this.board.betsDisplay.RefreshChips();
				},
			})
		} else {
			this.track.trackBetsDisplay.RefreshChips();
			this.board.setVisible(true)
			this.board.alpha = 1;
			this.tweens.add({
				targets: this.board,
				duration: tweenDuration,
				alpha: 0,
				onComplete: () => {
					this.board.setVisible(Client.logic.IsBoard);
					this.board.betsDisplay.RefreshChips();
				},
			})
		}

		if (!Client.logic.IsBoard) {
			this.track.setVisible(true);
			this.track.alpha = 0;
			this.tweens.add({
				targets: this.track,
				duration: tweenDuration,
				alpha: 1,
				onComplete: () => {
					this.track.setVisible(!Client.logic.IsBoard);
					this.track.positionTrackHotSpots();
					this.track.trackBetsDisplay.RefreshChips();
				}
			})
		} else {
			this.track.setVisible(true)
			this.track.alpha = 1;
			this.tweens.add({
				targets: this.track,
				duration: tweenDuration,
				alpha: 0,
				onComplete: () => {
					this.track.setVisible(!Client.logic.IsBoard);
					this.track.positionTrackHotSpots();
					this.track.trackBetsDisplay.RefreshChips();
				}
			})
		}
	}

	protected initialBackgroundMusic() {
		this.backgroundMusic = []
		if (Client.logic.DefaultAudio.toLowerCase() == "funky") {
			this.backgroundMusic.push(this.sound.add(Constants.SOUNDS.ALTERNATIVE_BACKGROUND_MUSIC, { loop: true, volume: 0.10 * musicVolume }) as any);
			this.backgroundMusic.push(this.sound.add(Constants.SOUNDS.DEFAULT_BACKGROUND_MUSIC, { loop: true, volume: 0.10 * musicVolume }) as any);
		} else {
			this.backgroundMusic.push(this.sound.add(Constants.SOUNDS.DEFAULT_BACKGROUND_MUSIC, { loop: true, volume: 0.10 * musicVolume }) as any);
			this.backgroundMusic.push(this.sound.add(Constants.SOUNDS.ALTERNATIVE_BACKGROUND_MUSIC, { loop: true, volume: 0.10 * musicVolume }) as any);
		}
		let intro_to_background_music = this.sound.add(Constants.SOUNDS.INTRO_TO_BACKGROUND_MUSIC, { loop: false, volume: 0.10 * musicVolume });

		intro_to_background_music.once("complete", () => this.backgroundMusic[this.backgroundMusicIndex].play());
		intro_to_background_music.play();
	}

	public update(time: number, delta: number): void {
		if (this.introPrompt && this.introPrompt.visible && !this.activeQuestionDialog) {
			if (this.lockScreen) this.lockScreen.BringToTop();
			this.children.bringToTop(this.introPrompt);
		}
		else {
			if (this.board && this.board.visible) this.board.Update(delta)
			if (this.track && this.track.visible) this.track.Update(delta)
			if (this.panel) this.panel.Update(delta)

			if (this.lockScreen) this.lockScreen.BringToTop();
			if (this.passwordDialog && this.passwordDialog.Visible) this.passwordDialog.BringToTop()
			if (this.winDisplay) this.winDisplay.Update(delta)

			if (this.activeQuestionDialog && this.activeQuestionDialog.visible) {
				this.children.bringToTop(this.activeQuestionDialog);
				if (this.panel && !virtualWheel)//my god this is ugly
					this.panel.HideVideo();
			}
		}

		Client.logic.update(time,delta);
	}

	/*
	 * Camera and canvas sizes are always height/width of the screen no matter the mode to fill the entire screen,
	 * all of the things inside of the canvas however switch width and height around when rotated (using staticHeight/staticWidth)
	 * to always keep the same ratio and positions eaven in portrait mode
	*/
	override resize(scaling: CrazyMegaEpicScalingStructure): void {
		super.resize(scaling);
		this.positionElements(scaling);
	}

	protected positionElements(scaling: CrazyMegaEpicScalingStructure): void {
		let ratio = 16 / 9;
		let minW = Math.min(this.staticWidth, this.staticHeight * ratio);
		let minH = Math.min(this.staticHeight, this.staticWidth / ratio);
		let useW = Math.min(minW, minH * ratio);

		if (this.portraitMode) {
			if (this.board) {
				this.board.OnResize(useW, useW / ratio, this.portraitMode, this.staticWidth,this.staticHeight);
				this.board.x = this.staticWidth / 2 - this.board.width / 2
				this.board.y = -(useW / ratio) + ((useW / ratio) - this.board.height);
			}
			if (this.track) {
				this.track.OnResize(useW, useW / ratio, this.board.width, this.board.y, this.portraitMode, this.board.height);
				this.track.x = this.staticWidth / 2 - this.track.width / 2
			}
		}
		else {
			if (this.board) {
				this.board.OnResize(useW, useW / ratio, this.portraitMode, this.staticWidth,this.staticHeight);
				this.board.x = (scaling.width - minW) / 2;
				this.board.y = this.staticHeight - this.board.height;

			}
			if (this.track) {
				this.track.OnResize(useW, useW / ratio, this.board.width, this.board.y, this.portraitMode, this.board.height);
				this.track.x = (scaling.width - minW) / 2;
			}
		}

		this.resizeText(this.staticWidth, this.staticHeight, this.portraitMode);

		if (this.miscButtons)
			this.miscButtons.OnResize(this.staticWidth, this.staticHeight, this.portraitMode, this.board.y, this.board.height);

		if (this.panel) {
			this.panel.UpdatePositions(this.staticWidth, this.staticHeight - this.board.height, this.portraitMode, this.staticWidth, this.staticHeight);
			this.panel.setPosition(0, 0);
			if (this.portraitMode) {
				this.panel.y -= this.staticHeight;
			}
		}

		if (this.passwordDialog)
			this.passwordDialog.Resize(this.staticWidth, this.staticHeight)


		if (this.winDisplay && this.board) {
			if (this.portraitMode)
				this.winDisplay.OnResize(0 - this.board.height / 1.5, this.staticWidth / 2, this.board.height, this.board.width, this.staticWidth, this.staticHeight, this.portraitMode);
			else
				this.winDisplay.OnResize(this.staticHeight - this.board.height / 1.5, this.staticWidth / 2, this.board.height, this.board.width, this.staticWidth, this.staticHeight, this.portraitMode);
		}

		if (this.panel) {
			this.children.sendToBack(this.panel);//panel must be over board and track for history expander to work
			this.children.sendToBack(this.board);
			this.children.sendToBack(this.track);
		}
		if (this.demoModeText) {
			this.demoModeText.OnResize(this.staticWidth, this.staticHeight, this.portraitMode);
			this.children.sendToBack(this.demoModeText);
		}
		if (this.backgroundImage)
			this.children.sendToBack(this.backgroundImage);

		if (this.historyDialog) {
			this.historyDialog.UpdatePositions(this.portraitMode, this.staticWidth, this.staticHeight);
		}

		if (this.messageController) {
			if (this.board) {
				this.messageController.setSize(this.staticWidth * 0.8, this.board.displayHeight * 0.8);
			}

			if (this.portraitMode) {
				this.messageController.y = - this.messageController.displayHeight;
				this.messageController.portraitMode = true;
			}
			else {
				this.messageController.y = this.staticHeight - this.messageController.displayHeight;
				this.messageController.portraitMode = false;
			}
			this.messageController.x = this.staticWidth * 0.1;
			this.children.bringToTop(this.messageController);
		}

		if (this.activeQuestionDialog && this.activeQuestionDialog.visible) {
			this.activeQuestionDialog.OnResize(this.staticWidth, this.staticHeight, this.portraitMode);
			this.children.bringToTop(this.activeQuestionDialog);
			if (this.portraitMode)
				this.activeQuestionDialog.y -= this.staticHeight;
		}

		if (Client.slot) {
			if (this.slotExitButton) {
				this.children.bringToTop(this.slotExitButton);
				this.slotExitButton.setOrigin(0);
				this.slotExitButton.setDisplaySize(100, 78);
				this.slotExitButton.scale = this.board.scale;
				this.slotExitButton.setPosition(this.scale.width - (this.slotExitButton.displayWidth + 10), 20);
			}
		}
	}

	public goBack(ms?: number): void {
		if (ms) {
			setTimeout(() => {
				this.goBack();
			}, ms)
		}
		else {
			try {
				let retVal = GetParameterByName("ret")
				if (retVal && retVal.length > 1)
					window.open(retVal, "_self")
				else if (window.history.length > 2)
					window.history.back()
			}
			catch (e) { }
		}
	}

	protected override AutoResolve() {
		super.AutoResolve();

		if (this.activeQuestionDialog && this.activeQuestionDialog.visible)
			this.activeQuestionDialog.ResponseSelected(0);
	}
}
